import { Decoration, EditorView, WidgetType } from "@codemirror/view";

function clampPosition(position, docLength) {
    if (typeof position !== "number" || Number.isNaN(position)) {
        return 0;
    }
    return Math.max(0, Math.min(docLength, position));
}

function toRgba(hexColor, alpha) {
    const normalized = String(hexColor || "#2563eb").replace("#", "");
    const expanded =
        normalized.length === 3
            ? normalized
                  .split("")
                  .map((value) => `${value}${value}`)
                  .join("")
            : normalized.padEnd(6, "0").slice(0, 6);
    const red = Number.parseInt(expanded.slice(0, 2), 16) || 0;
    const green = Number.parseInt(expanded.slice(2, 4), 16) || 0;
    const blue = Number.parseInt(expanded.slice(4, 6), 16) || 0;
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

class RemoteCursorWidget extends WidgetType {
    constructor(label, color) {
        super();
        this.label = label;
        this.color = color;
    }

    eq(other) {
        return other.label === this.label && other.color === this.color;
    }

    toDOM() {
        const wrap = document.createElement("span");
        wrap.className = "manuscript-remote-cursor";
        wrap.setAttribute("aria-hidden", "true");

        const caret = document.createElement("span");
        caret.className = "manuscript-remote-cursor__caret";
        caret.style.background = this.color;
        wrap.appendChild(caret);

        const label = document.createElement("span");
        label.className = "manuscript-remote-cursor__label";
        label.style.background = this.color;
        label.textContent = this.label;
        wrap.appendChild(label);

        return wrap;
    }

    ignoreEvent() {
        return true;
    }
}

export function buildCodePresenceExtension(remoteCursors) {
    const decorations = [];
    const normalizedCursors = Array.isArray(remoteCursors) ? remoteCursors : [];

    const decorationSet = EditorView.decorations.compute([], (state) => {
        const nextDecorations = [];

        for (const cursor of normalizedCursors) {
            if (!cursor || !cursor.participant) {
                continue;
            }

            const color = cursor.color || "#2563eb";
            const label = cursor.participant.name || "Collaborator";
            const docLength = state.doc.length;
            const anchor = clampPosition(cursor.anchor ?? cursor.from ?? cursor.head, docLength);
            const head = clampPosition(cursor.head ?? cursor.to ?? cursor.anchor, docLength);
            const from = Math.min(anchor, head);
            const to = Math.max(anchor, head);
            const caretPos = clampPosition(head, docLength);

            if (from !== to) {
                nextDecorations.push(
                    Decoration.mark({
                        attributes: {
                            style: `background:${toRgba(color, 0.18)};border-bottom:1px solid ${color};`,
                        },
                    }).range(from, to),
                );
            }

            nextDecorations.push(
                Decoration.widget({
                    widget: new RemoteCursorWidget(label, color),
                    side: 1,
                }).range(caretPos),
            );
        }

        return Decoration.set(nextDecorations, true);
    });

    decorations.push(decorationSet);
    decorations.push(
        EditorView.baseTheme({
            ".manuscript-remote-cursor": {
                position: "relative",
                display: "inline-block",
                width: "0",
                height: "0",
                overflow: "visible",
                verticalAlign: "top",
                pointerEvents: "none",
            },
            ".manuscript-remote-cursor__caret": {
                position: "absolute",
                left: "-1px",
                top: "-1px",
                width: "2px",
                height: "1.55em",
                borderRadius: "999px",
                boxShadow: "0 0 0 1px rgba(255,255,255,0.7)",
            },
            ".manuscript-remote-cursor__label": {
                position: "absolute",
                left: "-1px",
                top: "-1.5em",
                transform: "translateY(-2px)",
                display: "inline-flex",
                alignItems: "center",
                height: "16px",
                padding: "0 6px",
                borderRadius: "999px",
                color: "#ffffff",
                fontSize: "10px",
                fontWeight: "700",
                lineHeight: 1,
                whiteSpace: "nowrap",
                boxShadow: "0 4px 10px rgba(15, 23, 42, 0.18)",
            },
        }),
    );

    return decorations;
}
